import Vuetify from "@/plugins/vuetify";
import { DiagnosticRange } from "@/constants/enums/DiagnosticRange";
import i18n from "@/i18n";
import { ChartLinesColorsDark, Red } from "@/components/charts/ChartStyles";

/**
* @summary - Verifica se o resultSetId do esquema novo do diagnóstico é válido.
* @param {Objetct} resultSet - Objeto de Result Set.
* @returns {Boolean} - Verdadeiro caso o result set novo seja diferente de nulo.
*/
const IsNewResultSetValid = function(resultSet)
{
	return resultSet.newResultSet !== null;
};

/**
* @summary - Verifica se o resultSetId do esquema antigo do diagnóstico é válido.
* @param {Objetct} bearingDataObj - Objeto que contém informação de mancal.
* @returns {Boolean} - As informações, caso o mancal seja válido.
*/
const IsBearingIdValid = function(bearingDataObj)
{
	return !!bearingDataObj ? bearingDataObj.bearingId : 0;
};

/**
* @summary - Verifica se o Operating Range é igual a undefined.
* @param {Objetct} operatingRange - Status do indicador.
* @returns {Boolean} - Verdadeiro caso o Operating Range seja indefinido.
*/
const IsOperatingRangeUndefined = function(operatingRange)
{
	return operatingRange === DiagnosticRange.undefined;
};

/**
* @summary - Verifica se o Operating Range é igual a disabled.
* @param {Objetct} operatingRange - Status do indicador.
* @returns {Boolean} - Verdadeiro caso o Operating Range seja disabled.
*/
const IsOperatingRangeDisabled = function(operatingRange)
{
	return operatingRange === DiagnosticRange.disabled;
};

/**
* @summary - Verifica se o diálogo de análise detalhada pode ser renderizado.
* @param {Objetct} operatingRange - Status do indicador.
* @returns {Boolean} - Verdadeiro caso o Operating Range seja diferente de disabled ou undefined.
*/
const CanShowDialog = function(operatingRange)
{
	return !(IsOperatingRangeUndefined(operatingRange) || IsOperatingRangeDisabled(operatingRange));
};

const OrderLayoutModel = () =>
{
	const layout = {
		annotations:   [],
		paper_bgcolor: "rgba(0,0,0,0)",
		plot_bgcolor:  "rgba(0,0,0,0)",
		color:         "",
		showlegend:    true,
		shapes:        [],
		legend:        {
			x:           0,
			y:           1.2,
			orientation: "h",
			font:        {
				family: "Segoe UI",
				size:   18,
				color:  "",
			},
		},
		margin: {
			l:   70,
			r:   40,
			b:   50,
			t:   0,
			pad: 0,
		},
		xaxis: { title: i18n.t("Labels.XAxisName") },
		yaxis: { title: i18n.t("Labels.YAxisNameG") },
	};
	return layout;
};

const FrequencyLayoutModel = () =>
{
	const layout = {
		annotations:   [],
		paper_bgcolor: "rgba(0,0,0,0)",
		plot_bgcolor:  "rgba(0,0,0,0)",
		color:         "",
		showlegend:    true,
		shapes:        [],
		legend:        {
			x:           0,
			y:           1.2,
			orientation: "h",
			font:        {
				family: "Segoe UI",
				size:   18,
				color:  "",
			},
		},
		margin: {
			l:   70,
			r:   40,
			b:   50,
			t:   0,
			pad: 0,
		},
		xaxis: { title: i18n.t("Labels.FrequencyXAxisName") },
		yaxis: { title: i18n.t("Labels.YAxisNameG") },
	};
	return layout;
};

/**
* @summary Cria um Json com os dados para formar o gráfico.
* @param {Array} chartData - Dados dos gráficos.
* @param {Array} isOrderChart - Indica se o eixo X é contruído em ordem ou frequência.
*/
const CreateChart = (chartData, isOrderChart) =>
{
	const chart = [];

	for (let i = 0; i < chartData.length; i++)
	{
		const trace = {
			x:           isOrderChart ? chartData[i].orderXAxis : chartData[i].frequencyXAxis,
			y:           chartData[i].yAxis,
			mode:        "lines",
			marker:      { color: ChartLinesColorsDark[i] },
			name:        chartData[i].name,
			frequencies: isOrderChart ? chartData[i].concernOrders : chartData[i].concernFrequencies,
		};

		chart.push(trace);
	}
	return chart;
};

/**
 * @summary Cria as anotações do HSS e atribui na váriavel layout.
 * @param {Array} concernFrequencies Array com as posiçoes do eixo x das anotações.
 * @return {Array} shaper - Linhas que serão incorporadas ao layout.
 */
const CreateShapes = (concernValues) =>
{
	const shapes = [];
	for (let i = 0; i < concernValues.length; i++)
	{
		const shape = {
			yref: "paper",
			type: "line",
			x0:   concernValues[i],
			x1:   concernValues[i],
			y0:   0, // Representa 0%
			y1:   1, // Representa 100%
			line: {
				color: Red,
				width: 2,
				dash:  "dot",
			},
			visible: false,
		};
		shapes.push(shape);
	}
	return shapes;
};

/**
 * @summary Cria os textos para as anotações
 * @param {Array - Numbers} orders - Posições das anotações no eixo Y.
 * @param {String} diagnosticLocation - Informa se é no HSS, IMS, LSS ou Gerador.
 * @return {Array - JSON} retorna um array de JSONs com dados necessários para criar os textos das anotações.
*/
const CreateAnnotationText = (concernFrequencies, isDarkMode, annotationName = "HSS") =>
{
	const annotations = [];
	for (let i = 0; i < concernFrequencies.length; i++)
	{
		// Seleciona a cor do texto
		const textColor = isDarkMode ? Vuetify.framework.theme.themes.dark.secondary :
			Vuetify.framework.theme.themes.light.info;

		// Cria o texto
		const text = `${i + 1}x ${annotationName}`;

		// Cria a anotação em texto.
		const annotation = {
			x:          concernFrequencies[i],
			y:          0.8, // 80 % do valor em Y
			yref:       "paper",
			mode:       "text",
			showlegend: false,
			text,
			showarrow:  false,
			visible:    false,
			textangle:  270,
			align:      "right",
			yanchor:    "top",
			xshift:     10,
			yshift:     30,
			font:       { color: textColor },
		};
		annotations.push(annotation);
	}
	return annotations;
};

export default {
	IsNewResultSetValid,
	IsBearingIdValid,
	IsOperatingRangeUndefined,
	IsOperatingRangeDisabled,
	CanShowDialog,
	OrderLayoutModel,
	FrequencyLayoutModel,
	CreateChart,
	CreateShapes,
	CreateAnnotationText,
};
